import { useState } from 'react'
import { downloadCSV } from '@/lib/download-file'
import { captureException } from '@/lib/errors'
import { generateTimeEntriesCSV } from '@/lib/export-time-entries'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

interface BaseOptions {
    periodLabel: string
    includeUser?: boolean
}

interface EntriesByUserOptions extends BaseOptions {
    entriesByUser: Record<string, TimeEntriesExpanded[]>
    entries?: never
}

interface EntriesOptions extends BaseOptions {
    entries: TimeEntriesExpanded[]
    entriesByUser?: never
}

type UseExportTimeEntriesOptions = EntriesByUserOptions | EntriesOptions

export function useExportTimeEntries(options: UseExportTimeEntriesOptions) {
    const { periodLabel, includeUser = true } = options
    const [isExporting, setIsExporting] = useState(false)

    const exportToCSV = async () => {
        setIsExporting(true)
        try {
            const allEntries = options.entries ?? Object.values(options.entriesByUser).flat()
            if (allEntries.length === 0) {
                return
            }

            const csv = generateTimeEntriesCSV(allEntries, { includeUser })
            const sanitizedLabel = periodLabel.replace(/[^a-zA-Z0-9-]/g, '-')
            const filename = `time-entries-${sanitizedLabel}.csv`
            await downloadCSV(csv, filename)
        } catch (error) {
            captureException('Export time entries failed', error)
        } finally {
            setIsExporting(false)
        }
    }

    return {
        exportToCSV,
        isExporting,
    }
}
